import { MediaBreakpoint } from 'craft/types';

export enum StyleHierarchy {
  Styleguide = 'Styleguide',
  Large = 'Large',
  Medium = 'Medium',
  Small = 'Small',
}

interface HierarchyRecord {
  fallbackStyle: StyleHierarchy;
  fallbackValue?: unknown;
}

const hierarchyOrder = [
  StyleHierarchy.Styleguide,
  StyleHierarchy.Large,
  StyleHierarchy.Medium,
  StyleHierarchy.Small,
];

export type StylesHierarchy = Record<string, HierarchyRecord>;

interface StyleguideHierarchyProps {
  css: Record<string, unknown>;
  styleguideCss: Record<string, unknown>;
  lgCss: Record<string, unknown>;
  mdCss: Record<string, unknown>;
  smCss: Record<string, unknown>;
  viewport: MediaBreakpoint;
}

const viewportIndexes = {
  [MediaBreakpoint.LG]: hierarchyOrder.indexOf(StyleHierarchy.Large),
  [MediaBreakpoint.MD]: hierarchyOrder.indexOf(StyleHierarchy.Medium),
  [MediaBreakpoint.SM]: hierarchyOrder.indexOf(StyleHierarchy.Small),
};

export const createStyleguideHierachy = ({
  css,
  styleguideCss,
  lgCss,
  mdCss,
  smCss,
  viewport,
}: StyleguideHierarchyProps): StylesHierarchy => {
  const styles = {
    [StyleHierarchy.Styleguide]: styleguideCss ?? {},
    [StyleHierarchy.Large]: lgCss ?? {},
    [StyleHierarchy.Medium]: mdCss ?? {},
    [StyleHierarchy.Small]: smCss ?? {},
  };

  const getOverridingObject = (cssProp: string): HierarchyRecord => {
    const referenceIndex = viewportIndexes[viewport];
    const referenceValue = (styles[hierarchyOrder[referenceIndex]] ?? {})[cssProp];
    // eslint-disable-next-line no-plusplus
    for (let i = viewportIndexes[viewport]; i > 0; i--) {
      const currentValue = (styles[hierarchyOrder[i]] ?? {})[cssProp];
      if (currentValue === undefined) {
        // skip, it's not the correct value
      } else if (referenceValue !== currentValue) {
        return { fallbackStyle: hierarchyOrder[i], fallbackValue: currentValue };
      }
    }
    const fallbackStyle = hierarchyOrder[0];
    const fallbackValue = styles[fallbackStyle][cssProp];
    return { fallbackStyle, fallbackValue };
  };

  return Object.fromEntries(
    Object.keys(css).map((cssProp) => {
      return [cssProp, getOverridingObject(cssProp)];
    }),
  );
};

const englishNames = {
  [StyleHierarchy.Styleguide]: 'Styleguide',
  [StyleHierarchy.Large]: 'Desktop',
  [StyleHierarchy.Medium]: 'Tablet',
  [StyleHierarchy.Small]: 'Mobile',
};

export const getCssResetStyleMessage = (level: StyleHierarchy) => {
  const currentIndex = hierarchyOrder.indexOf(level);
  if (currentIndex < 0) {
    return undefined;
  }
  return `Reset to ${englishNames[hierarchyOrder[currentIndex]]}`;
};
